<script setup lang="ts">
import { Brush, Clock, Hand, Home, Mic, Sparkles, UserRound, Video } from 'lucide-vue-next';
import type { Message } from '@/types/domain';
import type { SenderInfo } from '@/composables/useFamily';

defineProps<{
  message: Message;
  senderInfo?: SenderInfo;
  compact?: boolean;
}>();

const icons = {
  drawing: Brush,
  audio: Mic,
  video: Video,
  play_request: Sparkles,
  kind_reply: Hand
};

function statusLabel(status: Message['status']) {
  if (status === 'pending') return 'wartet auf Freigabe';
  if (status === 'approved') return 'freigegeben';
  if (status === 'rejected') return 'abgelehnt';
  return 'empfangen';
}

function statusClass(status: Message['status']) {
  if (status === 'pending') return 'bg-amber-100 text-amber-800';
  if (status === 'approved') return 'bg-emerald-100 text-emerald-800';
  if (status === 'rejected') return 'bg-rose-100 text-rose-800';
  return 'bg-sky-100 text-sky-800';
}

function typeLabel(type: Message['type']) {
  if (type === 'drawing') return 'Zeichnung';
  if (type === 'audio') return 'Audio';
  if (type === 'video') return 'Video';
  if (type === 'play_request') return 'Spielanfrage';
  return 'Reaktion';
}
</script>

<template>
  <article class="rounded-[28px] border border-orange-100 bg-white p-5 shadow-soft">
    <div class="flex items-center gap-4">
      <span
        class="grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-[28px] text-white shadow-sm md:h-28 md:w-28"
        :style="{ backgroundColor: senderInfo?.color ?? '#fb7185' }"
        aria-hidden="true"
      >
        <img v-if="senderInfo?.imageUrl" class="h-full w-full object-cover" :src="senderInfo.imageUrl" alt="" />
        <Home v-else-if="senderInfo" class="h-14 w-14 fill-white/40" aria-hidden="true" />
        <UserRound v-else class="h-14 w-14" aria-hidden="true" />
      </span>
      <div class="min-w-0 flex-1">
        <p v-if="senderInfo" class="text-2xl font-black text-heart-berry md:text-3xl">{{ senderInfo.name }}</p>
        <div class="flex flex-wrap items-center gap-2">
          <p class="text-lg font-black text-heart-ink">{{ message.content }}</p>
          <span
            class="rounded-full px-3 py-1 text-xs font-black uppercase"
            :class="statusClass(message.status)"
          >
            {{ statusLabel(message.status) }}
          </span>
        </div>
        <p class="mt-3 inline-flex items-center gap-2 text-sm font-bold text-slate-500">
          <Clock class="h-4 w-4" aria-hidden="true" />
          {{ new Date(message.createdAt).toLocaleDateString('de-DE') }}
        </p>
        <p class="mt-2 inline-flex items-center gap-2 rounded-full bg-heart-cream px-3 py-1 text-xs font-black uppercase text-heart-ink">
          <component :is="icons[message.type]" class="h-4 w-4" aria-hidden="true" />
          {{ typeLabel(message.type) }}
        </p>
      </div>
    </div>
  </article>
</template>
